/**
 * Notification Service
 *
 * API functions for notifications and preferences.
 */

import { API_CONFIG } from '../config/api';
import { handleAuthError } from '../utils/authErrorHandler';
import type {
    NotificationsResponse,
    UnreadCountResponse,
    NotificationPreferencesResponse,
    NotificationPreferencesMap,
    NotificationPreferencesUpdateMap,
    NotificationTypesResponse,
} from '../types/notification';

function checkAuth(res: Response) {
    if (res.status === 401) {
        handleAuthError()
        throw new Error('Unauthorized')
    }
}

/**
 * Get auth headers from the provided token getter
 */
async function getHeaders(getToken: () => Promise<string>): Promise<HeadersInit> {
    const token = await getToken();
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
}

/**
 * Get user's notifications
 */
export async function getNotifications(
    getToken: () => Promise<string>,
    options: { limit?: number; offset?: number; unreadOnly?: boolean } = {}
): Promise<NotificationsResponse> {
    const { limit = 20, offset = 0, unreadOnly = false } = options;

    const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        unreadOnly: unreadOnly.toString(),
    });

    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications?${params}`, {
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to fetch notifications');
    }

    return res.json();
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(
    getToken: () => Promise<string>
): Promise<number> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/unread-count`, {
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to fetch unread count');
    }

    const data: UnreadCountResponse = await res.json();
    return data.count;
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(
    getToken: () => Promise<string>,
    notificationId: string
): Promise<void> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to mark notification as read');
    }
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(
    getToken: () => Promise<string>
): Promise<{ count: number }> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/read-all`, {
        method: 'POST',
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to mark all notifications as read');
    }

    return res.json();
}

/**
 * Get user's notification preferences
 */
export async function getNotificationPreferences(
    getToken: () => Promise<string>
): Promise<NotificationPreferencesMap> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/preferences`, {
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to fetch notification preferences');
    }

    const data: NotificationPreferencesResponse = await res.json();
    return data.preferences;
}

/**
 * Update user's notification preferences
 */
export async function updateNotificationPreferences(
    getToken: () => Promise<string>,
    preferences: NotificationPreferencesUpdateMap
): Promise<NotificationPreferencesMap> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: await getHeaders(getToken),
        body: JSON.stringify({ preferences }),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to update notification preferences');
    }

    const data: NotificationPreferencesResponse = await res.json();
    return data.preferences;
}

/**
 * Get available notification types
 */
export async function getNotificationTypes(
    getToken: () => Promise<string>
): Promise<NotificationTypesResponse> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/notifications/types`, {
        headers: await getHeaders(getToken),
    });

    checkAuth(res)
    if (!res.ok) {
        throw new Error('Failed to fetch notification types');
    }

    return res.json();
}
